import { useEffect, useRef, useState } from 'react';
import { FaPen, FaTag, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import InlineNumberInput from 'common/components/inlineNumberInput/InlineNumberInput';
import InlineTextInput from 'common/components/inlineTextInput/InlineTextInput';
import { BODY_TOOLTIP } from 'Config';
import styles from './CustomTotalCell.module.scss';

interface Props {
    total: number;
    customTotalText?: string | null;
    customTotalTextUserName?: string | null;
    decimalPlaces: number;
    disabled: boolean;
    onChangeCustomText: (value: string | null) => void;
}

function CustomTotalCell({ total, customTotalText, customTotalTextUserName, decimalPlaces, disabled, onChangeCustomText }: Props): JSX.Element {
    const { t } = useTranslation();
    // Fallback to the logged-in user when the line hasn't been persisted yet
    // (the server only fills customTotalTextUserName after save).
    const loggedInUserName = useSelector<Reducers, string | null>(s => s.authentication.profile?.realName ?? null);
    const hasCustomText = typeof customTotalText === 'string' && customTotalText.length > 0;
    const displayUserName = customTotalTextUserName ?? loggedInUserName ?? '';
    const activeTooltip = t('budgets.budget.lines.custom_total.tooltip_active', { name: displayUserName });
    const [editing, setEditing] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const showTextInput = hasCustomText || editing;

    // Focus the contentEditable inside InlineTextInput when entering edit mode.
    useEffect(() => {
        if (editing && wrapperRef.current) {
            const editable = wrapperRef.current.querySelector<HTMLElement>('[contenteditable="true"]');
            editable?.focus();
        }
    }, [editing]);

    const onBlurText = (value: string | null | undefined) => {
        const trimmed = (value ?? '').trim();
        if (trimmed.length === 0) {
            if (hasCustomText) {
                onChangeCustomText(null);
            }
            setEditing(false);
        } else if (trimmed !== customTotalText) {
            onChangeCustomText(trimmed);
        }
    };

    const onClear = () => {
        if (hasCustomText) {
            onChangeCustomText(null);
        }
        setEditing(false);
    };

    if (showTextInput) {
        return (
            <div
                ref={wrapperRef}
                className={styles.overrideContainer}
                data-tooltip-id={hasCustomText ? BODY_TOOLTIP : undefined}
                data-tooltip-content={hasCustomText ? activeTooltip : undefined}
            >
                <FaTag className={styles.tagIcon} />
                <div className={styles.textInputWrapper}>
                    <InlineTextInput
                        value={customTotalText ?? ''}
                        onBlur={onBlurText}
                        placeholder={t('budgets.budget.lines.custom_total.placeholder')}
                        disabled={disabled}
                    />
                </div>
                {!disabled && (
                    <button
                        type='button'
                        className={styles.clearButton}
                        onMouseDown={e => e.preventDefault()}
                        onClick={onClear}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={t('budgets.budget.lines.custom_total.clear')}
                        aria-label={t('budgets.budget.lines.custom_total.clear')}
                    >
                        <FaTimes />
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.numberWrapper}>
                <InlineNumberInput
                    value={total}
                    className={'text-center'}
                    type='money'
                    decimalScale={decimalPlaces}
                    disabled
                />
            </div>
            {!disabled && (
                <button
                    type='button'
                    className={styles.editButton}
                    onClick={() => setEditing(true)}
                    data-tooltip-id={BODY_TOOLTIP}
                    data-tooltip-content={t('budgets.budget.lines.custom_total.enter')}
                    aria-label={t('budgets.budget.lines.custom_total.enter')}
                >
                    <FaPen />
                </button>
            )}
        </div>
    );
}

export default CustomTotalCell;
